import {
  EntityLinks,
  Goal,
  goalInboxID,
  GoalProgressCount,
  GoalSortingMode,
  GroupByMode,
  ID,
  LifeArea,
} from '@griply/types';

import { buildParentToChildrenMap, getSubGoals } from './goals/get-sub-goals';
import { hasGoalParent } from './goals/has-goal-parent';
import { isActiveLifeArea } from './is-active-life-area';
import { sortGoals } from './sort-goals';
import { sortItems } from './sort-items';

export type BuildGoalSectionsOptions = {
  goals: Goal[];
  lifeAreas: LifeArea[];
  goalSortingMode?: GoalSortingMode;
  goalRoadmapSorting?: { inProgress?: ID[] | null } | null;
  goalGroupBy?: GroupByMode;
  goalGroupSorting?: {
    lifeArea?: { sorting?: Record<string, ID[]> | null } | null;
  } | null;
  lifeAreaSorting?: ID[] | null;
  taskCounts?: Map<ID, number>;
  goalProgressCounts?: Map<ID, GoalProgressCount>;
  // Without them a migrated sub-goal resolves no parent and lands in the main-goal list.
  links: EntityLinks;
};

export const buildGoalSections = (
  options: BuildGoalSectionsOptions,
): { id: string; items: [] }[] => {
  const {
    goals,
    lifeAreas,
    goalSortingMode,
    goalRoadmapSorting,
    goalGroupBy,
    goalGroupSorting,
    lifeAreaSorting,
    taskCounts,
    goalProgressCounts,
    links,
  } = options;

  const mainGoals: Goal[] = [];
  const subGoals: Goal[] = [];
  goals.forEach((goal) => {
    (hasGoalParent(goal, { links }) ? subGoals : mainGoals).push(goal);
  });

  // let: reassigned when goalGroupBy === LifeArea
  let sortedMainGoals = sortGoals(mainGoals, {
    allGoals: goals,
    sortMode: goalSortingMode,
    customOrder: goalRoadmapSorting?.inProgress,
    taskCounts,
    goalProgressCounts,
    links,
  });

  if (goalGroupBy === GroupByMode.LifeArea) {
    // An archived or frozen life area is not a group; its goals fall to the no-life-area bucket.
    const sortedLifeAreas = sortItems(
      lifeAreas.filter(isActiveLifeArea),
      lifeAreaSorting,
      'id',
    );
    sortedMainGoals = [
      ...sortedLifeAreas
        .map(({ id }) =>
          sortGoals(
            mainGoals.filter(({ lifeAreaId }) => lifeAreaId === id),
            {
              allGoals: goals,
              sortMode: goalSortingMode,
              customOrder: goalGroupSorting?.lifeArea?.sorting?.[id] ?? [],
              taskCounts,
              goalProgressCounts,
              links,
            },
          ),
        )
        .flat(1),
      ...sortGoals(
        mainGoals.filter(({ lifeAreaId }) => !lifeAreaId),
        {
          allGoals: goals,
          sortMode: goalSortingMode,
          customOrder: goalGroupSorting?.lifeArea?.sorting?.null ?? [],
          taskCounts,
          goalProgressCounts,
          links,
        },
      ),
    ];
  }

  const parentToChildrenMap = buildParentToChildrenMap(subGoals, links);
  // `visited` guards against a corrupt parent cycle sending the walk infinite.
  const visited = new Set<ID>();

  const addSortedSubGoals = (parentGoal: Goal, allSubGoals: Goal[]): Goal[] => {
    if (visited.has(parentGoal.id)) {
      return [];
    }
    visited.add(parentGoal.id);

    const directSubGoals = getSubGoals(parentGoal.id, {
      goals: allSubGoals,
      links,
      parentToChildrenMap,
    });

    if (!directSubGoals.length) {
      return [];
    }

    const sortedDirectSubGoals = sortGoals(directSubGoals, {
      allGoals: goals,
      sortMode: parentGoal.goalSortingMode,
      customOrder: parentGoal.goalSorting,
      taskCounts,
      goalProgressCounts,
      links,
    });

    const result: Goal[] = [];

    for (const subGoal of sortedDirectSubGoals) {
      result.push(subGoal);
      result.push(...addSortedSubGoals(subGoal, allSubGoals));
    }

    return result;
  };

  const orderedGoals = sortedMainGoals.reduce<Goal[]>((acc, goal) => {
    acc.push(goal);
    acc.push(...addSortedSubGoals(goal, subGoals));
    return acc;
  }, []);

  const goalOrder = orderedGoals.map((goal) => goal.id);

  return [
    ...sortItems(goals, goalOrder, 'id').map((goal) => ({
      id: goal.id,
      items: [] as [],
    })),
    {
      id: goalInboxID,
      items: [] as [],
    },
  ];
};
